import { useTranslation } from '@silospot/i18n/react';
import type { ConvMode, GoalCategory } from '@silospot/shared';
import { useQuery } from '@tanstack/react-query';
import { Target } from 'lucide-react';
import { type ReactElement, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Badge,
  Btn,
  Card,
  CardHead,
  EstimatedNote,
  Kpi,
  KpiGrid,
  PageHeader,
  Segmented,
} from '../../components/cld';
import { AreaChart } from '../../components/cld/charts';
import { Flag } from '../../components/location';
import { api } from '../../lib/api';
import { usePeriodRange } from '../../stores/period-store';

const CAT_COLOR: Record<string, string> = {
  purchase: '#2563eb',
  cart: '#0891b2',
  form: '#16a34a',
  newsletter: '#7c3aed',
  phone: '#d97706',
  download: '#db2777',
  custom: '#64748b',
};

export function ConversionsPage(): ReactElement {
  const { t } = useTranslation('pages');
  const { t: ts } = useTranslation('shell');
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const range = usePeriodRange();
  const [mode, setMode] = useState<ConvMode>('valid');

  const { data, isLoading } = useQuery({
    queryKey: ['cld-conversions', id, range, mode],
    queryFn: () => api.getConversions(id, range, mode),
    enabled: !!id,
  });

  const nf = new Intl.NumberFormat('fr-FR');
  const money = (cents: number) => `${nf.format(Math.round(cents / 100))} €`;
  const cat = (c: GoalCategory) => t(`cat.${c}`);
  const header = (
    <PageHeader
      title={ts('nav.conversions')}
      sub={t('conv.subtitle')}
      actions={
        <Segmented
          value={mode}
          onChange={setMode}
          options={[
            { value: 'valid', label: t('conv.valid') },
            { value: 'raw', label: t('conv.raw') },
          ]}
        />
      }
    />
  );

  if (isLoading || !data) {
    return (
      <div>
        {header}
        <Card>
          <div className="h-64 animate-pulse rounded-lg bg-neutral-bg" />
        </Card>
      </div>
    );
  }

  if (!data.hasGoals) {
    return (
      <div>
        {header}
        <Card className="mx-auto max-w-md py-10 text-center">
          <span className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-2xl bg-accent-bg text-primary">
            <Target className="h-6 w-6" />
          </span>
          <div className="text-[16px] font-bold text-text">{t('conv.emptyTitle')}</div>
          <p className="mx-auto mt-1 max-w-xs text-[13px] text-text-2">{t('conv.emptyBody')}</p>
          <div className="mt-4">
            <Btn primary onClick={() => navigate(`/projects/${id}/settings`)}>
              {t('conv.emptyCta')}
            </Btn>
          </div>
        </Card>
      </div>
    );
  }

  const k = data.kpis;
  const maxFunnel = Math.max(1, ...data.funnel.map((s) => s.count));
  const maxType = Math.max(1, ...data.types.map((s) => s.count));

  return (
    <div>
      {header}

      {mode === 'raw' && k.ghosts > 0 ? (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-[#f3c9c9] bg-fraud-bg px-4 py-3 text-[12.5px] text-fraud-fg">
          <span>⚠</span>
          <span>{t('conv.ghostBanner', { n: nf.format(k.ghosts) })}</span>
        </div>
      ) : null}

      <KpiGrid cols={4}>
        <Kpi label={t('conv.kpiRate')} value={`${nf.format(k.rate)} %`} />
        <Kpi label={t('conv.kpiConversions')} value={nf.format(k.conversions)} />
        <Kpi label={t('conv.kpiGhosts')} value={nf.format(k.ghosts)} />
        <Kpi label={t('conv.kpiAvg')} value={money(k.avgValue)} />
      </KpiGrid>

      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-[1.4fr_1fr]">
        <Card>
          <CardHead
            title={t('conv.funnelTitle')}
            sub={mode === 'valid' ? t('conv.funnelClean') : t('conv.funnelAll')}
          />
          <div className="flex flex-col gap-2.5">
            {data.funnel.map((s) => (
              <div key={s.key}>
                <div className="mb-1 flex justify-between text-[12.5px]">
                  <span className="text-[#3a4151]">{t(`conv.step${cap(s.key)}`)}</span>
                  <span className="font-mono text-text-2">{nf.format(s.count)}</span>
                </div>
                <div className="h-6 overflow-hidden rounded-md bg-neutral-bg">
                  <div
                    className="h-full rounded-md bg-gradient-to-r from-primary to-accent-strong"
                    style={{ width: `${(s.count / maxFunnel) * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </Card>
        <Card>
          <CardHead title={t('conv.typesTitle')} />
          <div className="flex flex-col gap-2.5">
            {data.types.map((s) => (
              <div key={s.category}>
                <div className="mb-1 flex justify-between text-[12.5px]">
                  <span className="inline-flex items-center gap-1.5 text-[#3a4151]">
                    <span
                      className="h-2.5 w-2.5 rounded-[3px]"
                      style={{ background: CAT_COLOR[s.category] ?? '#64748b' }}
                    />
                    {cat(s.category)}
                  </span>
                  <span className="font-mono text-text-2">{nf.format(s.count)}</span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-neutral-bg">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${(s.count / maxType) * 100}%`,
                      background: CAT_COLOR[s.category] ?? '#64748b',
                    }}
                  />
                </div>
              </div>
            ))}
            {data.types.length === 0 ? (
              <p className="py-3 text-center text-[13px] text-text-3">—</p>
            ) : null}
          </div>
        </Card>
      </div>

      <Card className="mt-4">
        <CardHead
          title={t('conv.rateTitle')}
          sub={mode === 'raw' ? t('conv.rateRaw') : undefined}
        />
        <AreaChart
          labels={data.series.map((p) => p.t.slice(5))}
          series={[
            {
              name: t('conv.kpiConversions'),
              color: '#2563eb',
              data: data.series.map((p) => p.conversions),
            },
          ]}
          height={220}
        />
      </Card>

      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card className="p-0">
          <div className="p-4 pb-0">
            <CardHead title={t('conv.sourcesTitle')} />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[13px]">
              <thead>
                <tr>
                  {[
                    t('conv.colSource'),
                    t('conv.colFraud'),
                    t('conv.colConv'),
                    t('conv.colRate'),
                  ].map((h, i) => (
                    <th
                      key={h}
                      className={`border-b border-border px-4 py-2.5 text-[11px] font-semibold uppercase text-text-3 ${i === 0 ? 'text-left' : 'text-right'}`}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.sources.map((s) => {
                  const fraud = s.visits > 0 ? Math.round((s.suspect / s.visits) * 100) : 0;
                  const rate =
                    s.visits > 0 ? Math.round((s.conversions / s.visits) * 1000) / 10 : 0;
                  return (
                    <tr key={s.sourceType} className="hover:bg-[#fafbfc]">
                      <td className="border-b border-[#f1f3f6] px-4 py-2.5 text-[#3a4151]">
                        {s.sourceType}
                      </td>
                      <td
                        className="border-b border-[#f1f3f6] px-4 py-2.5 text-right font-mono"
                        style={{
                          color:
                            fraud >= 40
                              ? 'var(--fraud-fg)'
                              : fraud >= 20
                                ? 'var(--suspect-fg)'
                                : 'var(--clean-fg)',
                        }}
                      >
                        {fraud} %
                      </td>
                      <td className="border-b border-[#f1f3f6] px-4 py-2.5 text-right font-mono">
                        {nf.format(s.conversions)}
                      </td>
                      <td className="border-b border-[#f1f3f6] px-4 py-2.5 text-right font-mono text-text-2">
                        {nf.format(rate)} %
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>

        <Card className="p-0">
          <div className="p-4 pb-0">
            <CardHead title={t('conv.recentTitle')} />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[13px]">
              <thead>
                <tr>
                  {[
                    t('conv.colTime'),
                    t('conv.colType'),
                    t('conv.colCountry'),
                    t('conv.colAmount'),
                  ].map((h, i) => (
                    <th
                      key={h}
                      className={`border-b border-border px-4 py-2.5 text-[11px] font-semibold uppercase text-text-3 ${i >= 3 ? 'text-right' : 'text-left'}`}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.recent.map((r, i) => (
                  <tr key={`${r.occurredAt}-${i}`} className="hover:bg-[#fafbfc]">
                    <td className="whitespace-nowrap border-b border-[#f1f3f6] px-4 py-2.5 font-mono text-[12px] text-text-3">
                      {r.occurredAt.slice(5, 16)}
                    </td>
                    <td className="border-b border-[#f1f3f6] px-4 py-2.5">
                      <Badge tone="clean">{cat(r.category)}</Badge>
                    </td>
                    <td className="whitespace-nowrap border-b border-[#f1f3f6] px-4 py-2.5">
                      <Flag country={r.country} /> {r.country || '—'}
                    </td>
                    <td className="border-b border-[#f1f3f6] px-4 py-2.5 text-right font-mono text-text">
                      {r.value > 0 ? money(r.value) : '—'}
                    </td>
                  </tr>
                ))}
                {data.recent.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-[13px] text-text-3">
                      —
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <div className="mt-4">
        <EstimatedNote />
      </div>
    </div>
  );
}

function cap(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
